import { showActionsModal } from './Modal.js';

// Placeholder chess renderer: a programmatic two-tone board + Unicode piece glyphs,
// generated art swaps in later. Pure view — it never talks to the network; it calls
// back `onMove({from,to,promo?})` (square = row*8+col, row 0 = rank 8 / TOP, matching
// the engine's OUR-square convention) once the player has picked a legal move.
//
// The engine's match_state carries NO board array — only `state.fen` (via the engine
// `extras` hook). We derive piece placement client-side from the FEN placement field
// (no chess rules needed — a plain string split). `state.check` tints my king red.
//
// Orientation: like the checkers renderer, we ORIENT so MY colour sits at the BOTTOM.
// For 'w' the FEN rank-8-first order already puts white at the bottom (identity); for
// 'b' we rotate the mapping 180° ((r,c)->(7-r,7-c)) — mapping only, glyphs unchanged.
// Rotation is parity-preserving so the light/dark square pattern is drawn once and is
// correct in both orientations. Spectators (null) see the default 'w'-at-bottom view.

// Art-swap-ready geometry (like checkersAssets): a future board image with a frame
// would set these to its measured inset. The programmatic board is frameless: full box.
const BOARD_INNER_OFFSET_FRAC = 0;
const BOARD_INNER_SIZE_FRAC = 1;
const LIGHT_SQUARE = 0xf0d9b5;   // classic light maple
const DARK_SQUARE = 0xb58863;    // classic walnut

// Unicode chess glyphs, keyed by FEN letter (uppercase = white, lowercase = black).
const GLYPH = {
  K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘', P: '♙',
  k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟',
};

// Initial per-colour piece counts (king excluded — it is never captured). Used to
// derive captured-piece icons: missing opponent pieces = pieces THIS colour has taken.
const INITIAL_COUNTS = { p: 8, n: 2, b: 2, r: 2, q: 1 };

// FEN placement field -> 64-array of FEN letters (or null), index = row*8+col with
// row 0 = rank 8. FEN lists ranks 8->1 separated by '/', files a->h left to right, so
// the mapping is direct — no rules knowledge, just the split.
function parseFen(fen) {
  const board = new Array(64).fill(null);
  if (typeof fen !== 'string') return board;
  const placement = fen.split(' ')[0];
  const ranks = placement.split('/');
  for (let r = 0; r < 8 && r < ranks.length; r++) {
    let c = 0;
    for (const ch of ranks[r]) {
      if (ch >= '1' && ch <= '8') c += Number(ch);
      else { if (c < 8) board[r * 8 + c] = ch; c += 1; }
    }
  }
  return board;
}

export function createChessRenderer(scene, { x, y, size, myColor, onMove }) {
  const offset = size * BOARD_INNER_OFFSET_FRAC;
  const sq = (size * BOARD_INNER_SIZE_FRAC) / 8;
  const left = x - size / 2;
  const top = y - size / 2;
  const fs = Math.floor(sq * 0.8);

  let flip = myColor === 'b';
  let color = myColor ?? null;      // my colour ('w'|'b'|null spectator)
  let board = new Array(64).fill(null);
  let legalMoves = [];              // [{from,to,promo?}] for the side to move (mine only)
  let interactive = false;
  let check = false;
  let selection = null;            // engine square of my picked piece, or null
  let busy = false;                // true while the promotion modal is open

  // engine (r,c) -> display (dr,dc), and inverse.
  const toDisplay = (r, c) => (flip ? [7 - r, 7 - c] : [r, c]);
  const toEngine = (dr, dc) => (flip ? [7 - dr, 7 - dc] : [dr, dc]);

  function squareCentre(square) {
    const r = Math.floor(square / 8), c = square % 8;
    const [dr, dc] = toDisplay(r, c);
    return { cx: left + offset + (dc + 0.5) * sq, cy: top + offset + (dr + 0.5) * sq };
  }

  const container = scene.add.container(0, 0).setDepth(10);
  // Board squares: drawn once (parity is rotation-invariant, so one draw fits both
  // orientations). Coloured by DISPLAY parity: (dr+dc) even = light. a8 (top-left,
  // display 0,0) is light; a1 is dark — the real board colouring.
  const boardGfx = scene.add.graphics();
  for (let dr = 0; dr < 8; dr++) {
    for (let dc = 0; dc < 8; dc++) {
      boardGfx.fillStyle((dr + dc) % 2 === 0 ? LIGHT_SQUARE : DARK_SQUARE, 1);
      boardGfx.fillRect(left + offset + dc * sq, top + offset + dr * sq, sq, sq);
    }
  }
  const highlights = scene.add.graphics();
  const pieceLayer = scene.add.container(0, 0);
  container.add([boardGfx, highlights, pieceLayer]);

  const inner = size * BOARD_INNER_SIZE_FRAC;
  const zone = scene.add.zone(left + offset + inner / 2, top + offset + inner / 2, inner, inner)
    .setInteractive({ useHandCursor: true });
  container.add(zone);
  // pointerUP, not pointerdown: a tap that opens the promotion picker must not leak
  // its own release into the just-created modal overlay (which closes on pointerup) —
  // the same idiom every hall click-zone (counter/chairs/ranking) already uses.
  zone.on('pointerup', (pointer) => {
    if (!interactive || busy) return;
    const dc = Math.floor((pointer.worldX - (left + offset)) / sq);
    const dr = Math.floor((pointer.worldY - (top + offset)) / sq);
    if (dr < 0 || dr > 7 || dc < 0 || dc > 7) return;
    const [r, c] = toEngine(dr, dc);
    onSquareTap(r * 8 + c);
  });

  function targets() {
    if (selection == null) return [];
    return [...new Set(legalMoves.filter((m) => m.from === selection).map((m) => m.to))];
  }

  async function promptPromotion(from, to, promoCands) {
    // Same from/to, four variants differing only by promo — ask which piece.
    const PICKS = [
      { promo: 'q', label: 'Hetman ♕' },
      { promo: 'r', label: 'Wieża ♖' },
      { promo: 'b', label: 'Goniec ♗' },
      { promo: 'n', label: 'Skoczek ♘' },
    ].filter((p) => promoCands.some((m) => m.promo === p.promo));
    busy = true;
    const chosen = await showActionsModal(scene, {
      title: 'Awans piona', body: 'Wybierz figurę', actions: PICKS.map((p) => ({ label: p.label, fn: () => p.promo })),
    });
    busy = false;
    if (chosen) onMove({ from, to, promo: chosen });
    else redraw();   // cancelled — restore the (already cleared) selection view
  }

  function onSquareTap(square) {
    if (busy) return;
    if (selection == null) {
      if (legalMoves.some((m) => m.from === square)) { selection = square; redraw(); }
      return;
    }
    if (square === selection) { selection = null; redraw(); return; }   // tap again = deselect
    const cands = legalMoves.filter((m) => m.from === selection && m.to === square);
    if (cands.length === 0) {
      // Not a legal target. A fresh own piece restarts the selection; else clear.
      selection = legalMoves.some((m) => m.from === square) ? square : null;
      redraw();
      return;
    }
    const from = selection;
    selection = null;
    if (cands.some((m) => m.promo)) {
      promptPromotion(from, square, cands.filter((m) => m.promo));   // leaves board mid-await
    } else {
      onMove({ from, to: square });   // resulting match_state refreshes the board
    }
  }

  function redraw() {
    pieceLayer.removeAll(true);
    for (let s = 0; s < 64; s++) {
      const v = board[s];
      if (!v) continue;
      const glyph = GLYPH[v];
      if (!glyph) continue;
      const isWhite = v === v.toUpperCase();
      const { cx, cy } = squareCentre(s);
      const t = scene.add.text(cx, cy, glyph, {
        fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${fs}px`,
        color: isWhite ? '#fafafa' : '#181818',
      }).setOrigin(0.5);
      // Subtle contrast: white pieces get a dark stroke + shadow so they read on light
      // squares; black pieces a faint light rim so they read on dark squares.
      if (isWhite) { t.setStroke('#1a1a1a', Math.max(2, fs * 0.035)); t.setShadow(0, 1, '#000000', 2, false, true); }
      else t.setStroke('#e8e8e8', Math.max(1, fs * 0.02));
      pieceLayer.add(t);
    }

    highlights.clear();
    const drawSquare = (square, col, alpha) => {
      const { cx, cy } = squareCentre(square);
      highlights.fillStyle(col, alpha);
      highlights.fillRect(cx - sq / 2, cy - sq / 2, sq, sq);
    };
    // My king in check (only on my turn — the flag is about the side to move).
    if (interactive && check && color) {
      const kingChar = color === 'w' ? 'K' : 'k';
      const ks = board.indexOf(kingChar);
      if (ks >= 0) drawSquare(ks, 0xff3b30, 0.55);
    }
    if (interactive && selection != null) {
      drawSquare(selection, 0xffd166, 0.55);
      for (const t of targets()) drawSquare(t, 0x3ad07a, 0.5);
    }
  }

  // Same update() surface as the checkers renderer (board is ignored — chess derives
  // placement from state.fen; state also carries the check flag).
  function update({ legalMoves: lm, interactive: on, myColor: mc, state: st }) {
    if (mc !== undefined) { color = mc ?? null; flip = mc === 'b'; }
    if (st && typeof st.fen === 'string') board = parseFen(st.fen);
    legalMoves = lm || [];
    interactive = !!on;
    check = !!(st && st.check);
    selection = null;   // a new state always resets any in-progress pick
    redraw();
  }

  // Captured-piece icons for a panel: pieces THIS colour has taken = opponent pieces
  // missing from the board vs the initial set. Returns a Unicode string (possibly
  // empty) — the scene renders it as a glyph row. Null when we have no board yet so
  // the scene keeps its generic (0) fallback rather than reporting a full capture.
  function capturedGlyphs(st, panelColor) {
    if (!st || typeof st.fen !== 'string') return null;
    const b = parseFen(st.fen);
    const oppIsWhite = panelColor === 'w' ? false : true;   // captured pieces are opponent-colour
    const present = { p: 0, n: 0, b: 0, r: 0, q: 0 };
    for (const v of b) {
      if (!v) continue;
      const lower = v.toLowerCase();
      if (!(lower in present)) continue;
      const vIsWhite = v === v.toUpperCase();
      if (vIsWhite === oppIsWhite) present[lower] += 1;
    }
    let out = '';
    for (const t of ['q', 'r', 'b', 'n', 'p']) {
      const missing = Math.max(0, INITIAL_COUNTS[t] - present[t]);
      out += GLYPH[oppIsWhite ? t.toUpperCase() : t].repeat(missing);
    }
    return out;
  }

  // Sfx key for a state transition (BoardGameScene hook — chess has no board array,
  // so the scene's count-based checkers heuristic can't see captures; we diff FENs).
  // Promotion is exact, not inferred from pawn count: only a promotion increases the
  // non-pawn piece count (a pawn captured EN ROUTE also lowers pawn count, and a
  // capture-promotion lowers total too — both would fool simpler count rules).
  function moveSfx(prevState, nextState) {
    const counts = (st) => {
      if (!st || typeof st.fen !== 'string') return null;
      let total = 0, pawns = 0;
      for (const ch of st.fen.split(' ')[0]) {
        if (/[a-z]/i.test(ch)) { total += 1; if (ch === 'p' || ch === 'P') pawns += 1; }
      }
      return { total, nonPawns: total - pawns };
    };
    const a = counts(prevState), b = counts(nextState);
    if (!a || !b) return 'chess_move';
    if (b.nonPawns > a.nonPawns) return 'promote_piece';
    if (b.total < a.total) return 'chess_capture';
    return 'chess_move';
  }

  function clearSelection() {
    if (selection != null) { selection = null; redraw(); }
  }

  function destroy() {
    zone.destroy();
    container.destroy(true);
  }

  return {
    update, clearSelection, destroy, capturedGlyphs, moveSfx,
    // e2e hook: drive the exact tap->(picker)->onMove path without a real pointer.
    tapSquare: (square) => onSquareTap(square),
    getSelection: () => selection,
  };
}
